'use client';
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ElowenApiError, elowenClient } from '../../lib/elowenClient';
import type { ChatTurn } from '../../lib/transcript';
import type { BrainMessage, Profile } from '../../lib/types';
import type { ReplyQuoteState } from './ReplyQuote';

const NOTHING: readonly string[] = [];

/** Resolve transcript replies and an optional pending composer target, by target id.
 *  The caller supplies a pending id only when it belongs to the active session.
 *
 *  A target among the loaded turns is quoted from them. One outside them is read once through
 *  `GET /brain/messages/:id`, cached under a key that names its conversation, so an answer for one
 *  conversation is never drawn in another. A 404 (the message is gone) or a target the jump could not reach
 *  (`gone`) is unavailable; any other failed read leaves the quote's own retry in its place, so a daemon
 *  that did not answer never reads as a deleted message. The quote is a region of the transcript: its failure
 *  is stated there, never also as a toast, and a quote already read stays when a later read fails. A user message without `author` was written
 *  in a 1:1 conversation, by its owner; until
 *  the conversation's membership names that owner the quote keeps loading. */
export function useReplyQuotes(turns: readonly ChatTurn[], session: string | null, owner: Profile | null, gone: ReadonlySet<string>, pendingTargetId?: string | null): ReadonlyMap<string, ReplyQuoteState> {
  const { targets, loaded } = useMemo(() => {
    const targets = new Set<string>();
    const loaded = new Map<string, ChatTurn>();
    for (const turn of turns) {
      if ((turn.role === 'you' || turn.role === 'elowen') && turn.replyTo) targets.add(turn.replyTo.id);
      if ((turn.role === 'you' || turn.role === 'elowen') && turn.id) loaded.set(turn.id, turn);
    }
    if (pendingTargetId) targets.add(pendingTargetId);
    return { targets: targets.size ? [...targets] : NOTHING, loaded };
  }, [turns, pendingTargetId]);
  const missing = session === null ? NOTHING : targets.filter((id) => !loaded.has(id) && !gone.has(id));
  const reads = useQueries({
    queries: missing.map((id) => ({
      queryKey: ['brain-message', session, id] as const,
      queryFn: () => elowenClient.brainMessage(session as string, id),
      retry: false,
      staleTime: Infinity,
    })),
  });

  const quotes = new Map<string, ReplyQuoteState>();
  const user = (author: Profile | undefined, text: string): ReplyQuoteState => {
    const profile = author ?? owner;
    return profile ? { status: 'ready', author: { kind: 'user', profile }, text } : { status: 'loading' };
  };
  const fromMessage = (message: BrainMessage): ReplyQuoteState =>
    message.role === 'user' ? user(message.author, message.text) : { status: 'ready', author: { kind: 'agent' }, text: message.text };
  for (const id of targets) {
    const turn = loaded.get(id);
    if (gone.has(id)) quotes.set(id, { status: 'unavailable' });
    else if (turn?.role === 'you') quotes.set(id, user(turn.author, turn.text));
    else if (turn?.role === 'elowen') {
      const text = turn.segments.flatMap((segment) => (segment.kind === 'text' ? [segment.text] : [])).join('\n');
      quotes.set(id, { status: 'ready', author: { kind: 'agent' }, text });
    }
  }
  missing.forEach((id, index) => {
    const read = reads[index]!;
    const gone = read.error instanceof ElowenApiError && read.error.status === 404;
    quotes.set(id, read.data ? fromMessage(read.data) : read.isError
      ? (gone ? { status: 'unavailable' } : { status: 'failed', retry: () => void read.refetch() }) : { status: 'loading' });
  });
  return quotes;
}
